import { groupBy } from 'lodash';
import { useContext } from 'react';
import { VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';
import { DEFAULT_FONT_FAMILY, type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontFamilySelector, type FontItem } from '@/components/font-family-selector';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { VectorPanelContext } from '../../context';
import { Tracker } from '../../utils';

type Props = {
    fonts: FontItem[];
    enableRemoteSearch?: boolean;
};

const FALLBACK_FONT_FAMILY = DEFAULT_FONT_FAMILY().name;

export const FontFamily: React.FC<Props> = ({ fonts, enableRemoteSearch = true }) => {
    const { layerId, recordId, isVIP, isIntl, onOpenVIPModal } = useContext(VectorPanelContext)!;

    const editor = useEditor();
    const fontFamily = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        return textInfo ? textInfo.fontFamily : null;
    }) as string | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const usingFonts = editor.font.getUsingList(editor.active.getActiveOriginIndex());

    const handleOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openFontFamilySelector({
                recordId,
                stageType: editor.stage.getType(),
            });
        }
    };

    const handleChange = (data: FontItem, fontName: string) => {
        if (!fontFamily) return;

        editor.getInstance(VectorLayer).setFontFamily(layerId, fontName, String(data['material_id']));

        Tracker.setFontFamily({
            recordId,
            stageType: editor.stage.getType(),
            font: data,
            isSearch: false,
            isVectorLayer: true,
        });
    };

    return (
        <FontFamilySelector
            value={fontFamily || FALLBACK_FONT_FAMILY}
            fonts={groupBy(fonts, 'category_name')}
            usingFonts={usingFonts}
            enableRemoteSearch={!isIntl && enableRemoteSearch}
            isVip={isVIP}
            onClickVipBtn={onOpenVIPModal}
            onChange={handleChange}
            onDropdownVisibleChange={handleOpenChange}
        />
    );
};
